import { generateFramedWall, type WallOpening } from "./generateFramedWall";
import { createRectRoofGeometry } from "../roof/rectRoofGeometry";
import { resolveWallHeightM } from "../geometry/buildingDimensions";
import type { BuildInput, CutRow } from "../../engines/types";
import { DEFAULT_ROOF_CONFIG_BY_TYPE } from "../roof/roofConfig";

/** The same stud intervals and local roof clipping used by wall rendering. */
export function calculateWallStudCuts(
	input: Pick<
		BuildInput,
		"dimensions" | "roofConfig" | "openings" | "wallHeightM"
	>,
): CutRow[] {
	const { width: W, length: L } = input.dimensions;
	const roofConfig = input.roofConfig ?? DEFAULT_ROOF_CONFIG_BY_TYPE.fyrkantig;
	const WALL_H_M = resolveWallHeightM(input.wallHeightM);
	const roof = createRectRoofGeometry({
		width: W,
		length: L,
		wallHeight: WALL_H_M,
		roofConfig,
	});
	return (["front", "back", "left", "right"] as const).map((wall) => {
		const gable = roofConfig.kind === "gable";
		const frameInput = {
			wallLengthMm: (wall === "front" || wall === "back" ? W : L) * 1000,
			wallHeightMm: Math.round(
				(gable ? WALL_H_M : roof.getMaxWallHeight(wall)) * 1000,
			),
			openings: input.openings.filter((o: WallOpening) => o.wall === wall),
		};
		let frame;
		try {
			frame = generateFramedWall(frameInput);
		} catch {
			frame = generateFramedWall({ ...frameInput, openings: [] });
		}
		const lengths = frame.studs
			.filter((s) => s.type === "standard")
			.map((s) => {
				const x =
					wall === "left"
						? -W / 2
						: wall === "right"
							? W / 2
							: s.xMm / 1000 - W / 2;
				const z =
					wall === "front"
						? L / 2
						: wall === "back"
							? -L / 2
							: s.xMm / 1000 - L / 2;
				return Math.round(
					Math.min(
						s.yEndMm,
						(gable ? WALL_H_M : roof.getRoofHeightAt(x, z)) * 1000 - 45,
					) - s.yStartMm,
				);
			})
			.filter((n) => n > 0);
		return {
			part: `Stående Väggreglar (${wall === "front" ? "Framsida" : wall === "back" ? "Baksida" : "Sidor"}: 45×95)`,
			count: `${lengths.length} st`,
			cutLength: lengths.map((n) => `${n} mm`).join(" / "),
			materialKey: `framing.studs.${wall === "left" || wall === "right" ? "sides" : wall}`,
			purchaseDimension: "45x95",
			pieces: lengths.map((lengthMm) => ({ lengthMm, count: 1 })),
		};
	});
}
